/** Redemption form: validation and persistence through the repository port. */
import { joinMissingFieldLabelsEs } from '../../../core/domain/utils/joinMissingFieldLabelsEs.ts';
import { gpsCoordsValid } from '@core/domain/utils/gpsCoordsValid.ts';
import { ok, fail, type Result } from '@core/domain/utils/Result.ts';
import type { RedencionBonoRepositoryPort } from '../domain/ports/RedencionBonoRepositoryPort.ts';
import type { LocalPersistContext } from '@src/modules/shared/local-sqlite/domain/LocalPersistContext.ts';
import {
  flushLocalPersistAfterMutation,
  rejectIfStorageQuotaBlocksSave,
} from '@src/modules/shared/local-sqlite/application/localPersistAfterMutation.ts';
import { collectRedencionMediaDataUrls } from '@src/modules/sync/domain/estimateMediaPayloadBytes.ts';
import type {
  RedencionBonoCatalogSnapshot,
  RedencionBonoHeader,
  RedencionBonoListItem,
  RedencionBonoParticipanteFormPayload,
} from '../domain/redencionBonoModel.ts';
import type { ParticipanteBusquedaRedencionPrefill } from '../../shared/participante/domain/participanteLookupTypes.ts';
import type { ParticipanteBusquedaCandidate } from '../../shared/participante/domain/participanteBusquedaCandidate.ts';
import type { ParticipanteBusquedaPrefillResolve } from '../../shared/participante/domain/participanteBusquedaPrefillResolve.ts';

/** Collects validation issues for redemption participants. */
function collectParticipantesRedencionIssues(
  _cat: RedencionBonoCatalogSnapshot,
  _participantes: RedencionBonoParticipanteFormPayload[],
): string[] {
  return [];
}

/** Collects validation issues for the redemption header. */
function collectRedencionHeaderIssues(
  cat: RedencionBonoCatalogSnapshot,
  header: RedencionBonoHeader,
  participantesCount: number,
  participantes: RedencionBonoParticipanteFormPayload[],
  opts?: { requireParticipants?: boolean },
): string[] {
  const issues: string[] = [];
  if (header.entregaId == null) {
    issues.push('Número de entrega');
  } else if (!cat.entregas.some((e) => e.id === header.entregaId)) {
    issues.push('Número de entrega (valor no válido en el catálogo)');
  }
  if (header.esRemanente == null) {
    issues.push('¿Es remanente?');
  }
  if (!header.fecha.trim()) {
    issues.push('Fecha');
  }
  if (!header.responsableId || header.responsableId <= 0) {
    issues.push('Responsable');
  }
  if (header.proveedorId == null) {
    issues.push('Proveedor');
  } else {
    const prov = cat.proveedores.find((p) => p.id === header.proveedorId);
    if (!prov) {
      issues.push('Proveedor (valor no válido en el catálogo)');
    }
  }
  if (!gpsCoordsValid(header.latitud ?? '', header.longitud ?? '')) {
    issues.push('Ubicación (coordenadas GPS)');
  }
  if (opts?.requireParticipants !== false && participantesCount === 0) {
    issues.push('Al menos un participante');
  }
  issues.push(...collectParticipantesRedencionIssues(cat, participantes));
  return issues;
}

export class RedencionBonoApplicationService {
  /** Constructs this instance. */
  constructor(
    private readonly repo: RedencionBonoRepositoryPort,
    private readonly localPersist?: LocalPersistContext,
  ) {}
  /** Retrieves a list of all redemptions. */
  listRedenciones(): RedencionBonoListItem[] {
    return this.repo.listRedenciones();
  }
  /** Retrieves a specific redemption by its ID. */
  getRedencion(id: number): RedencionBonoListItem | null {
    return this.repo.getRedencion(id);
  }
  /** Retrieves the participants associated with a specific redemption. */
  listParticipantesForRedencion(redencionId: number): RedencionBonoParticipanteFormPayload[] {
    return this.repo.listParticipantesForRedencion(redencionId);
  }
  /** Finds the redemption ID associated with a participant. */
  findRedencionIdForParticipante(participanteId: number): number | null {
    return this.repo.findRedencionIdForParticipante(participanteId);
  }
  /** Another redemption for the same entrega + proveedor, or null when allowed. */
  findConflictingRedencionIdForParticipante(
    participanteId: number,
    entregaCatalogId: number,
    proveedorCatalogId: number,
    excludedRedencionId: number | null,
  ): number | null {
    return this.repo.findConflictingRedencionIdForParticipante(
      participanteId,
      entregaCatalogId,
      proveedorCatalogId,
      excludedRedencionId,
    );
  }
  /** Retrieves the snapshot of the redemption catalog. */
  getCatalog(): RedencionBonoCatalogSnapshot {
    return this.repo.getCatalog();
  }
  /** Prefill when searching redemption participants by document (PEP + redemption-line fallback inside repository). */
  findParticipanteRedencionPrefillByDocumento(documento: string): ParticipanteBusquedaRedencionPrefill | null {
    const t = documento.trim();
    if (!t) return null;
    return this.repo.findParticipanteRedencionPrefillByDocumento(t);
  }
  /** Resolves redemption participant prefill including ambiguous / not-found reasons. */
  resolveParticipanteRedencionBusquedaPrefill(
    documento: string,
  ): ParticipanteBusquedaPrefillResolve<ParticipanteBusquedaRedencionPrefill> {
    return this.repo.resolveParticipanteRedencionBusquedaPrefill(documento);
  }

  resolveParticipanteRedencionBusquedaPrefillByParticipanteId(
    participanteId: number,
  ): ParticipanteBusquedaPrefillResolve<ParticipanteBusquedaRedencionPrefill> {
    return this.repo.resolveParticipanteRedencionBusquedaPrefillByParticipanteId(participanteId);
  }

  listParticipanteBusquedaCandidates(docBusqueda: string): ParticipanteBusquedaCandidate[] {
    const t = docBusqueda.trim();
    if (!t) return [];
    return this.repo.listParticipanteBusquedaCandidates(t);
  }

  collectHeaderIssuesForParticipanteModal(header: RedencionBonoHeader): string[] {
    const cat = this.repo.getCatalog();
    return collectRedencionHeaderIssues(cat, header, 0, [], { requireParticipants: false });
  }
  /** Validates and saves a redemption and its participants. */
  async saveRedencion(
    editingId: number | null,
    header: RedencionBonoHeader,
    participantes: RedencionBonoParticipanteFormPayload[],
  ): Promise<{ ok: true; id: number } | { ok: false; message: string }> {
    const cat = this.repo.getCatalog();
    const issues = collectRedencionHeaderIssues(cat, header, participantes.length, participantes);
    if (issues.length > 0) {
      return { ok: false, message: joinMissingFieldLabelsEs(issues) };
    }

    const quotaBlock = await rejectIfStorageQuotaBlocksSave(
      this.localPersist,
      collectRedencionMediaDataUrls(header, participantes),
    );
    if (quotaBlock) return { ok: false, message: quotaBlock };

    if (editingId == null) {
      const id = this.repo.insertRedencion(header, participantes);
      await flushLocalPersistAfterMutation(this.localPersist);
      return { ok: true, id };
    }
    const row = this.repo.getRedencion(editingId);
    if (!row) return { ok: false, message: 'El registro a editar no existe.' };
    this.repo.updateRedencion(editingId, header, participantes);
    await flushLocalPersistAfterMutation(this.localPersist);
    return { ok: true, id: editingId };
  }

  /** Returns `bonos.nombre` for display when only the row id is available. */
  getBonoNombreById(bonoId: number): string | null {
    return this.repo.getBonoNombreById(bonoId);
  }

  /** Resolves `bonos.id` from the voucher caption (same rules as delivery-point capture). */
  resolveBonoIdForRedencionCaptura(caption: string, tipoBonoCatalogId: number): number | null {
    return this.repo.resolveBonoIdForRedencionCaptura(caption, tipoBonoCatalogId);
  }

  participanteTipoBonoCatalogId(participanteId: number): number | null {
    return this.repo.participanteTipoBonoCatalogId(participanteId);
  }

  /** Rows already synced successfully; safe to delete locally to reclaim space. */
  listRedencionesUploadedOffline(): RedencionBonoListItem[] {
    return this.repo.listRedencionesUploadedOffline();
  }

  countPendingOfflineExport(): number {
    return this.repo.countPendingOfflineExport();
  }

  /** Deletes a redemption only when {@link RedencionBonoListItem.offlineUploadedAt} was set after sync. */
  deleteUploadedRedencion(id: number): Result<void, string> {
    try {
      this.repo.deleteUploadedRedencion(id);
      return ok(undefined);
    } catch (e) {
      const m = e instanceof Error ? e.message : String(e);
      return fail(m || 'No se pudo eliminar la redención.');
    }
  }
}
